<script>
import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import { mapGetters, mapActions, mapState } from "vuex";
import moment from "moment";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Swal from "sweetalert2";


export default {
  page: {
    title: "LPO Details",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout },
  data() {
    return {
      id: atob(this.$route.params.id),
      isGeneratingPdf: false,
      whatsappTargetPhone: '',
    };
  },
  computed: {
    ...mapState("notification", ["type", "message", "show"]),
    ...mapGetters("stock", ["LPO"]),
    ...mapGetters("setting", ["GeneralSettings"]),
    ...mapGetters("auth", ["business", "branch"]),

    logoSource() {
        if (this.LPO && this.LPO.business.Businesslogo_base64) {
            return this.LPO.business.Businesslogo_base64;
        }
        if (this.LPO && this.LPO.business.Businesslogo) {
            return this.LPO.business.Businesslogo;
        }
        return null;
    }
  },

  watch: {
    show(newValue) {
      if (newValue == true) {
        if (this.type == "success") {
          Swal.fire("Success!", this.message.msg, "success");
        } else {
          Swal.fire("Error!", this.message.msg, "error");
        }
      }
    },
  },

  methods: {
    ...mapActions({
      FetchLPO: "stock/fetchLPODetails",
      FetchGeneralSettings: "setting/fetchGeneralSettings",
      notifySuccess: "notification/success", 
      notifyError: "notification/error",
    }),

    thousandSeperator(m) {
      if (m !== undefined && m !== null) {
        return m.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      }
      return '0';
    },

    formatPhoneNumber(phone) {
        if (!phone || !this.LPO?.business?.country?.dial_code) return null;
        const dialcode = this.LPO.business.country.dial_code;
        return dialcode + phone.toString().replace(/\D/g, '').slice(-9);
    },

    async generateAndSharePdf(shareType) {
        this.isGeneratingPdf = true;
        const lpoElement = document.getElementById('lpo-content-wrapper');

        if (!lpoElement) {
            this.notifyError({ msg: "Error: Could not find LPO content to generate PDF." });
            this.isGeneratingPdf = false;
            return;
        }

        try {        
          const canvas = await html2canvas(lpoElement, { scale: 2, useCORS: true, ignoreElements: (element) => element.classList.contains('d-print-none') });
          const imgData = canvas.toDataURL('image/jpeg', 1.0);
          const pdf = new jsPDF('p', 'mm', 'a4');
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();

           // Height of the canvas in PDF scale
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pdfHeight;

           // Add more pages if needed
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
          heightLeft -= pdfHeight;
        }
          const lpoFileName = `LPO-${this.LPO.supplierobj.name-this.LPO.supplierobj.contact}-${moment(new Date()).format("YYYY-MM-DD")}.pdf`;

          if(shareType === 'app'){
            const pdfBlob = pdf.output('blob');
            const file = new File([pdfBlob], lpoFileName, { type: 'application/pdf' });   
            if (navigator.share && navigator.canShare({ files: [file] })) {
              await navigator.share({
                title: `Purchase Order for ${this.LPO.supplierobj.name-this.LPO.supplierobj.contact}`,
                text: `Hello ${this.LPO.supplierobj.name-this.LPO.supplierobj.contact},\n\nPlease find our purchase order attached.\n\nThank you!`,
                files: [file],
              });
            } 
          }else if (shareType === 'email') {
            pdf.save(lpoFileName);
            const successMessage = "The purchase order has been downloaded as "+lpoFileName+". Please attach this file to your message.";
            this.notifySuccess({ msg: successMessage });
            const supplierEmail = this.LPO.supplierobj?.email || '';
            const subject = `Purchase Order from ${this.LPO.business.name}`;
            const body = `Hello ${this.LPO.supplierobj.name-this.LPO.supplierobj.contact},\n\nPlease find our Purchase Order attached.\n\nThank you.`;
            window.open(`mailto:${supplierEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
          } else if (shareType === 'whatsapp') {
            pdf.save(lpoFileName);
            const successMessage = "The purchase order has been downloaded as "+lpoFileName+". Please attach this file to your message.";
            this.notifySuccess({ msg: successMessage });
            const supplierPhone = this.LPO.supplierobj?.contact; 
            this.whatsappTargetPhone = this.formatPhoneNumber(supplierPhone);
            if (this.whatsappTargetPhone) {
                this.$bvModal.show('share-whatsapp-modal');
            } else {
                this.notifyError({ msg: "Supplier contact number not found for WhatsApp sharing." });
            }
          }
        } catch (error) {
          this.notifyError({ msg: "An error occurred while generating the PDF. Please ensure you are connected to the internet." });
        } finally {
          this.isGeneratingPdf = false;
        }
    },

    shareToWhatsApp(type) {
      const message = `Hello, please find attached our Purchase Order.`;
      const encodedMessage = encodeURIComponent(message);
      if (this.whatsappTargetPhone) {
          const url = type === 'app' 
              ? `whatsapp://send?phone=${this.whatsappTargetPhone}&text=${encodedMessage}` 
              : `https://web.whatsapp.com/send?phone=${this.whatsappTargetPhone}&text=${encodedMessage}`;
          if (type === 'app') window.location.href = url;
          else window.open(url, '_blank');
      }
      this.$bvModal.hide('share-whatsapp-modal');
    },

    printLPO() {
      window.print();
    },
  },

  created() {
    this.FetchLPO(this.id);
    this.FetchGeneralSettings();
  },
};
</script>

<template>
  <Layout>
    <div class="row">
      <div class="col-lg-12">
        <div :colspan="12" class="text-center" v-if="!LPO">
          <div class="text-warning my-2">
            <b-spinner class="align-middle"></b-spinner>
              <strong>Loading...</strong>
          </div>
        </div>
        <div v-else id="lpo-content-wrapper">
          <div class="action-buttons d-print-none mb-4 d-flex justify-content-end align-items-center">
            <b-button-group>
                <b-dropdown right variant="info" :disabled="isGeneratingPdf">
                    <template #button-content>
                        <i class="fa fa-share-alt me-1"></i> {{ isGeneratingPdf ? 'Generating...' : 'Share' }}
                    </template>
                    <b-dropdown-item @click="generateAndSharePdf('app')"><i class="fa fa-desktop me-2"></i>Any App</b-dropdown-item>
                    <b-dropdown-item @click="generateAndSharePdf('email')"><i class="fa fa-envelope me-2"></i>Email</b-dropdown-item>
                    <b-dropdown-item @click="generateAndSharePdf('whatsapp')"><i class="fab fa-whatsapp me-2"></i>WhatsApp</b-dropdown-item>
                </b-dropdown>
                <b-button variant="success" @click="printLPO"><i class="fa fa-print me-1"></i> Print</b-button>
                <b-button variant="warning" @click="$router.go(-1)"><i class="fa fa-arrow-left"></i>  Back</b-button>
            </b-button-group>
          </div>
          <div class="card" id="lpo-content">
            <div class="card-body p-2">
              <header class="lpo-header mb-5">
                <div class="row align-items-center">
                  <div class="col-md-3" align="center">
                     <img v-if="logoSource" :src="logoSource" height="70" alt="Company Logo" class="mb-3"/>
                  </div>
                  <div class="col-md-9 text-center">
                    <h4 v-if="GeneralSettings.show_businessname == 1" class="text-uppercase fw-bold font-size-24 text-success"><b>{{ business }}</b></h4>
                    <p class="mb-1 font-size-18">{{ LPO.branch.address }}</p>
                    <p class="mb-1 font-size-18"><strong>Tel:</strong> {{ LPO.contacts }}</p>
                    <p class="font-size-18"><strong>Email:</strong> {{ LPO.branch.email }}</p>
                  </div>
                </div>
                <div style="border-top: 1px solid black;"></div>
                <div class="row mt-4">
                  <div class="col-12 text-center">
                    <h1 class="fw-bold display-5 font-size-24">PURCHASE ORDER</h1><br>
                    <p class="font-size-18"><strong>Date:</strong> {{ LPO.dateAdded }}</p>
                  </div>
                </div>
              </header>
              <hr>
              <section class="supplier-info my-4">
                <h5 class="fw-bold font-size-24">SUPPLIER</h5>
                <p class="fw-bold mb-1">{{ LPO.supplier || '---' }}</p>
              </section>
              <div class="table-responsive">
                <table class="table table-bordered">
                  <thead class="bg-light">
                    <tr>
                      <th>S/No.</th>
                      <th>ITEM DESCRIPTION</th>
                      <th class="text-end">QUANTITY</th>
                      <th class="text-end">UNIT PRICE</th>
                      <th class="text-end">TOTAL</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in LPO.all" :key="item.id">
                      <td>{{ index + 1 }}</td>
                      <td>{{ item.product }}</td>
                      <td class="text-end">{{ item.qty }} {{ item.unit }}</td>
                      <td class="text-end">{{ thousandSeperator(item.unit_price) }}</td>
                      <td class="text-end fw-bold">{{ thousandSeperator(item.qty * item.unit_price) }}</td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr class="summary-row">
                      <td colspan="5">
                        <span class="label">TOTAL: </span>
                        <span class="value">{{LPO ? LPO.currency : ""}} {{ thousandSeperator(LPO.totAmt) }}</span>
                      </td>
                    </tr>
                    <tr class="summary-row">
                      <td colspan="5">
                        <span class="label">PAID: </span>
                        <span class="value">{{LPO ? LPO.currency : ""}} {{ thousandSeperator(LPO.paid) }}</span>
                      </td>
                    </tr>
                    <tr class="summary-row total-row">
                      <td colspan="5">
                        <span class="label">BALANCE DUE: </span>
                        <span class="value">{{LPO ? LPO.currency : ""}} {{ thousandSeperator(LPO.totAmt - LPO.paid) }}</span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <footer class="mt-3">
                <div class="row">
                  <div class="col-md-6"></div>
                  <div class="col-md-6 text-md-end"><strong>Prepared by:</strong> {{ LPO.user }}</div>
                </div>
              </footer>
            </div>
          </div>
          <!-- modal share purchase order via WhatsApp -->
          <b-modal
              header-bg-variant="info"
              header-text-variant="light"
              body-text-variant="info"
              footer-bg-variant="info"
              footer-text-variant="info"
              id="share-whatsapp-modal"
              hide-footer
          >
            <template #modal-title> Share Purchase order Via WhatsApp</template>
            <div class="modal-content">
              <div class="modal-body">
                <p>The purchase order PDF has been downloaded to your device.</p>
                <p class="mb-0">How would you like to open WhatsApp to share it?</p>
              </div>
              <div class="modal-footer d-flex justify-content-center">
                <button type="button" class="btn btn-success" @click="shareToWhatsApp('app')">
                  <i class="fab fa-whatsapp me-2"></i>Open WhatsApp App
                </button>
                <button type="button" class="btn btn-secondary" @click="shareToWhatsApp('web')">
                  <i class="fa fa-desktop me-2"></i>Use WhatsApp Web
                </button>
              </div>
            </div>
          </b-modal>
          <!-- modal ends -->
        </div>
      </div>
    </div>
  </Layout>
</template>

<style>
#lpo-content .table th:first-child,
#lpo-content .table td:first-child {
  width: 70px;
}

/* Default alignment for summary section for all views */
#lpo-content tfoot .summary-row td {
  text-align: right !important;
  border: none !important;
  padding: 4px 8px !important;
}
#lpo-content tfoot .summary-row .label {
  font-weight: bold;
  margin-right: 8px; /* Add space between label and value */
}
#lpo-content tfoot .summary-row span {
  display: inline; /* Keep label and value on the same line */
}
#lpo-content tfoot .total-row {
  font-weight: bold;
  font-size: 1.05em;
}


/* --- STYLES FOR AUTOMATIC PRINT FORMATTING --- */
@media print {
  * { box-sizing: border-box; }
  .left-side-menu, .navbar-custom, .footer, .page-header, .d-print-none { display: none !important; }
  body { background-color: #fff !important; }
  .main-content, .page-content, .container-fluid { width: 100% !important; margin: 0 !important; padding: 0 !important; }
  #lpo-content-wrapper { border: none !important; box-shadow: none !important; padding: 0 !important; }
  .card { border: none !important; box-shadow: none !important; }

  /* --- FORMAT 1: Standard A4/A3/A5 Styles (No vertical borders) --- */
  @media (min-width: 150mm) {
    @page { margin: 15mm; }
    #lpo-content { font-size: 10pt !important; }
    #lpo-content .font-size-24 { font-size: 18pt !important; }
    #lpo-content .font-size-18 { font-size: 12pt !important; }
    #lpo-content .table tr { page-break-inside: avoid; }
    #lpo-content tfoot .total-row { font-size: 12pt; }
  }

  /* --- FORMAT 2: Receipt Printer Styles (WITH vertical borders) --- */
  @media (max-width: 100mm) {
    @page { margin: 0; }
    #lpo-content {
      width: 100% !important; padding: 0 3mm !important; 
      font-family: 'Courier New', Courier, monospace !important; font-size: 9pt !important; color: #000 !important;
      overflow-wrap: break-word; word-wrap: break-word; 
    }
    #lpo-content .row:not(.d-print-none) { display: block !important; }
    #lpo-content [class*="col-"]:not(.d-print-none) { width: 100% !important; padding: 0 !important; text-align: left !important; }
    #lpo-content .text-center { text-align: center !important; }
    #lpo-content .text-md-end { text-align: left !important; }
    #lpo-content img { max-width: 100% !important; height: auto !important; margin-bottom: 5px; }
    #lpo-content .font-size-24 { font-size: 12pt !important; font-weight: bold; }
    #lpo-content .font-size-18 { font-size: 10pt !important; line-height: 1.2; }
    #lpo-content h3, #lpo-content h5 { font-size: 10pt !important; font-weight: bold; }

    /* Definitive Table Styling for Receipt */
    #lpo-content .table {
      font-size: 8pt !important;
      table-layout: fixed;
      width: 100%;
      border-collapse: collapse;
    }
    /* ADDED: Vertical borders to table cells */
    #lpo-content .table th, 
    #lpo-content .table td {
      padding: 3px 2px !important;
      border-bottom: 1px dotted #000 !important;
      border-right: 1px dotted #000;
      word-wrap: break-word;
    }
    /* ADDED: Left border and narrow width for the first column */
    #lpo-content .table th:first-child, 
    #lpo-content .table td:first-child {
      border-left: 1px dotted #000;
      width: 10% !important;
    }
    
    /* Summary (tfoot) Section for Receipt */
    #lpo-content tfoot .summary-row td {
      border-top: 1px solid #000 !important;
      border-bottom: none !important;
      border-left: none !important; /* Remove grid lines from summary */
      border-right: none !important;
      padding: 5px 2px !important;
      font-size: 10pt !important;
    }
    
    /* Final cleanup rules */
    #lpo-content .table thead tr { border-bottom: 1px solid #000 !important; }
    #lpo-content div[style*="border-top"], hr { border-top: 1px dashed black !important; margin: 5px 0 !important; }
    #lpo-content .py-5, #lpo-content .py-3, #lpo-content .p-2, #lpo-content .py-4, #lpo-content .my-4 { 
      padding-top: 5px !important; padding-bottom: 5px !important; margin-top: 5px !important; margin-bottom: 5px !important;
    }
  }
}
</style>